const STATE_PREFIX = 'v2:';
const STORAGE_PREFIX = 'axle_oauth_state:';
const EXPIRATION_MS = 1000 * 60 * 60 * 24; // 24 hours

type StoredLinkState = {
  token: string;
  updatingTSP: string;
  magicLink: string;
  serviceID: string;
  fleetCode: string;
  createdAt: number;
};

export type LinkStatePayload = {
  token: string;
  updatingTSP: boolean | string;
  magicLink: boolean | string;
  serviceID?: string;
  fleetCode?: string;
};

export type ParsedLinkState = {
  token: string;
  updatingTSP: string;
  magicLink: string;
  serviceID: string;
  fleetCode: string;
  source: 'storage' | 'query';
};

const normalizeBoolean = (value: boolean | string): string =>
  typeof value === 'string' ? value : value ? 'true' : 'false';

const normalizeOptional = (value?: string | null): string => value || '';

const isSessionStorageAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return false;
    }

    const testKey = '__link_state_test__';
    window.sessionStorage.setItem(testKey, 'test');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

const generateStorageId = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return Math.random().toString(36).slice(2, 12);
};

const cleanupStaleEntries = (storage: Storage) => {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !key.startsWith(STORAGE_PREFIX)) {
        continue;
      }

      const rawValue = storage.getItem(key);
      if (!rawValue) {
        keysToRemove.push(key);
        continue;
      }

      try {
        const parsed = JSON.parse(rawValue) as StoredLinkState;
        if (!parsed.createdAt || now - parsed.createdAt > EXPIRATION_MS) {
          keysToRemove.push(key);
        }
      } catch (error) {
        // Malformed entry - remove it
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
  } catch (error) {
    // Swallow cleanup errors to avoid blocking OAuth flow
  }
};

export const createLinkState = (payload: LinkStatePayload): string => {
  const stateData: StoredLinkState = {
    token: payload.token,
    updatingTSP: normalizeBoolean(payload.updatingTSP),
    magicLink: normalizeBoolean(payload.magicLink),
    serviceID: normalizeOptional(payload.serviceID),
    fleetCode: normalizeOptional(payload.fleetCode),
    createdAt: Date.now(),
  };

  if (isSessionStorageAvailable()) {
    try {
      const storage = window.sessionStorage;
      cleanupStaleEntries(storage);

      const id = generateStorageId();
      storage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(stateData));

      return `${STATE_PREFIX}${id}`;
    } catch (error) {
      // Fall back to legacy format if sessionStorage fails
    }
  }

  // Legacy fallback keeps previous behaviour (large state string)
  return [
    stateData.token,
    stateData.updatingTSP,
    stateData.magicLink,
    stateData.serviceID,
    stateData.fleetCode,
  ].join(',');
};

export const parseLinkState = (
  rawState: string | null | undefined
): ParsedLinkState | null => {
  if (!rawState) {
    return null;
  }

  if (rawState.startsWith(STATE_PREFIX)) {
    if (!isSessionStorageAvailable()) {
      return null;
    }

    const id = rawState.slice(STATE_PREFIX.length);
    const storageKey = `${STORAGE_PREFIX}${id}`;

    try {
      const storedValue = window.sessionStorage.getItem(storageKey);
      if (!storedValue) {
        return null;
      }

      window.sessionStorage.removeItem(storageKey);

      const parsed = JSON.parse(storedValue) as StoredLinkState;
      return {
        token: parsed.token,
        updatingTSP: parsed.updatingTSP,
        magicLink: parsed.magicLink,
        serviceID: parsed.serviceID,
        fleetCode: parsed.fleetCode,
        source: 'storage',
      };
    } catch (error) {
      return null;
    }
  }

  const [
    token = 'null',
    updatingTSP = 'false',
    magicLink = 'false',
    serviceID = 'null',
    fleetCode = 'null',
  ] = rawState.split(',');

  return {
    token,
    updatingTSP,
    magicLink,
    serviceID,
    fleetCode,
    source: 'query',
  };
};
